import { create } from "zustand";
import { Copy as CopyIcon } from "lucide-react";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { cn } from "@/lib/utils";

interface CopyStore {
    copiedId: string | null;
    setCopiedId: (id: string | null) => void;
}

export const useCopyStore = create<CopyStore>((set) => ({
    copiedId: null,
    setCopiedId: (id) => {
        set({ copiedId: id });
        // Reset after animation
        setTimeout(() => set({ copiedId: null }), 300);
    },
}));

interface CopyButtonProps {
    value: string;
    id: string;
    disabled?: boolean;
    className?: string;
}

export function CopyButton({ value, id, disabled = false, className }: CopyButtonProps) {
    const toastCopy = useToastCopy();
    const setCopiedId = useCopyStore((state) => state.setCopiedId);

    const handleCopy = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopiedId(id);
        toast.success(toastCopy.copiedToClipboard);
    };

    return (
        <button
            type="button"
            onClick={handleCopy}
            disabled={disabled}
            className={cn(
                "p-1 rounded-md w-8 h-8 text-slate-600 hover:text-slate-700 cursor-pointer",
                disabled && "cursor-not-allowed pointer-events-none",
                className
            )}
            aria-label={toastCopy.copiedToClipboard}
        >
            <CopyIcon size={18} />
        </button>
    );
}

interface CopyableTextProps {
    id: string;
    children: React.ReactNode;
    className?: string;
}

export function CopyableText({ id, children, className }: CopyableTextProps) {
    const copiedId = useCopyStore((state) => state.copiedId);
    const isCurrentlyCopied = copiedId === id;

    return <span className={cn(isCurrentlyCopied && "animate-wiggle", className)}>{children}</span>;
}

interface CopyProps {
    id: string;
    value: string;
    children: React.ReactNode;
    className?: string;
}

export function Copy({ id, value, children, className }: CopyProps) {
    return (
        <div className={cn("group relative min-w-0 max-w-full", className)}>
            <CopyableText id={id} className="min-w-0">
                {children}
            </CopyableText>
            <CopyButton
                className="top-1/2 right-0 absolute opacity-0 focus-visible:opacity-100 group-hover:opacity-100 p-0.5 w-7 h-7 transition-opacity -translate-y-1/2 shrink-0"
                id={id}
                value={value}
            />
        </div>
    );
}
